import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useAtomValue } from 'jotai';
import type { ColumnDef } from '@tanstack/react-table';

import { DataTable } from '@/components/glitch-ui/data-table';
import type { WidgetConfig } from '@/components/glitch-ui/widget-grid';
import { useAdPerformanceReportQuery, metaCreativePerformanceQueryOptions, metaIntegrationStatusQueryOptions } from '@/lib/query-options';
import { formatMoney, toDisplayAmount } from '@/lib/performance/money';
import { deriveMetrics, formatRate, formatRoas } from '@/lib/performance/metrics';
import { pivotLeaves, availableDimensions, PIVOT_DIMENSIONS, type PivotDimension, type PivotLeaf, type PivotRow } from '@/lib/performance/pivot';
import { resolveDateWindow } from '@/lib/performance/date-window';
import { platformsInScope, type Platform, type PlatformScope } from '@/lib/performance/types';
import { PlatformLogo } from '@/lib/performance/platform';
import { MetaPerformanceBadge } from '@/components/campaign/meta/meta-metrics';
import type { MetaPerformanceStatus } from '@/lib/types';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { usePerformanceData, useScopedCampaigns, useNeverLiveAds } from '@/hooks/usePerformanceData';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { ColumnsPopover, parseCampaignName, MutedDash } from '../cells';
import { CreativeThumb } from '../creative-thumb';
import { cn } from '@/lib/utils';
import { SubViewSwitcher } from './sub-view-switcher';
import { AdsTab, type AdsSubView } from '../tabs/ads-tab';

/** The wrapper's views: the pivot's two dimensions plus the Google-only
    perf-v2 sub-views (09-09). */
type WrapperView = 'ads' | 'creatives' | AdsSubView;
import {
  consolidatedPerformanceDateRangeAtom,
  consolidatedPerformanceCustomRangeAtom,
  consolidatedPerformanceGroupByAtom,
  consolidatedPerformanceComparisonAtom,
  consolidatedPerformanceCampaignTypesAtom,
  consolidatedPerformanceCampaignIdsAtom,
  consolidatedPerformanceIncludeExternalAtom,
} from '@/store';

// Column-picker metadata for the Ads table. Duplicated from perf-v2
// (performance-v2.tsx:122-136) so the Google Ads view can drive the same
// column-visibility hook; de-dup tracked in #5994.
const ADS_TABLE_COLUMNS: { id: string; label: string; pinned?: boolean }[] = [
  { id: 'ad_name',     label: 'Ad',          pinned: true },
  { id: 'headlines',   label: 'Headlines' },
  // Consolidated Imp · Clicks · Conv · Cost cell. On by default; users can
  // switch back to individual columns via the column picker for delta arrows.
  { id: 'performance', label: 'Performance' },
  { id: 'impressions', label: 'Impressions' },
  { id: 'clicks',      label: 'Clicks' },
  { id: 'ctr',         label: 'CTR' },
  { id: 'conversions', label: 'Conversions' },
  { id: 'cost',        label: 'Cost' },
  { id: 'cpa',         label: 'CPA' },
];
const ADS_COLUMN_IDS = new Set(ADS_TABLE_COLUMNS.map(c => c.id));
const ADS_PINNED_COLUMN_IDS = new Set(ADS_TABLE_COLUMNS.filter(c => c.pinned).map(c => c.id));

// Column picker for the merged pivot (09-08): the superset of every id the
// pivot can render across scopes. Ids missing from this set fail OPEN (stay
// visible) so a new column can never vanish by omission.
const ADS_PIVOT_COLUMN_IDS = new Set([
  'platform', 'label', 'impressions', 'clicks', 'results', 'spend', 'roas', 'cpc',
  'ctr', 'imprToConv', 'clickToConv', 'cpa', 'purchases', 'costPerPurchase',
]);
const ADS_PIVOT_PINNED_IDS = new Set(['platform', 'label']);
const pivotColId = (c: ColumnDef<PivotRow, unknown>): string =>
  (c.id ?? (c as { accessorKey?: string }).accessorKey) as string;

// perf-v2 DEFAULT_LAYOUTS.ads tab id under the consolidated shell's shared
// widget-layout atom (see CONSOLIDATED_DEFAULT_LAYOUTS).
const GOOGLE_ADS_LAYOUT_KEY = 'google-ads';

function numOrNull(value: string | null | undefined): number | null {
  if (value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

// Meta-native per-ad extras (Frequency, winner/loser Status, Purchases) + the Google
// campaign subtitle — kept OUT of the generic pivot (pivot.ts stays platform-agnostic)
// and looked up by the ad's pivot key when grouping by Ad.
interface AdExtras {
  frequency: number | null;
  status: MetaPerformanceStatus | null;
  campaign: string | null;
  purchases: number;
  costPerPurchaseCents: number | null;
  /** Click-through destination (09-09): the ad's Glitch detail page for
      seeded drafts, the campaign page for reported rows (the light campaigns
      list carries no ads to resolve a Glitch ad id from — #4760). */
  href?: string | null;
  /** Meta creative asset (09-09): on Meta the image is the ad, so the row
      shows it. Null for Google, whose Search ads are text. */
  assetId?: number | null;
}

/**
 * Unified Creative & Ads table (#5971, CP-8; #6040, T2). A dimension pivot over the
 * same ad-level data (Google ads + Meta creatives), grouped by Ad (default) or
 * Platform, rendering the canonical metric block (metrics.ts). Grouped by Ad under
 * Meta scope it also shows the Meta-native Frequency + winner/loser Status. Campaign
 * and Ad-group grouping live on the Campaigns tab — Meta creative rows carry no
 * campaign linkage — so this panel only offers Ad + Platform.
 */
export function CreativeAdsPanel({
  scope,
  toolbarLeading,
  forcedDimension,
}: {
  scope: PlatformScope;
  /** Unified-switcher hosting (09-09): the wrapper owns the dimension, so the
      panel renders exactly that view and keeps its Columns control. */
  forcedDimension?: PivotDimension;
  /** All-scope hosting (09-08): the outer sub-view switcher rides this
      table's own toolbar row instead of taking a row of its own. */
  toolbarLeading?: React.ReactNode;
}) {
  const inScope = platformsInScope(scope);
  const googleInScope = inScope.includes('google');
  const metaInScope = inScope.includes('meta');
  const { symbol } = useCurrencySymbol();
  const cs = symbol ?? '';

  // Honor the toolbar date range through the shared exact-day window (#6274).
  // Meta stays snapshot-backed.
  const selectedDays = useAtomValue(consolidatedPerformanceDateRangeAtom);
  const customRange = useAtomValue(consolidatedPerformanceCustomRangeAtom);
  const window = useMemo(() => resolveDateWindow(selectedDays, customRange), [selectedDays, customRange]);

  // #6277 — same campaign scoping as Google scope (was account-wide `undefined`,
  // so the scope switch changed the Google numbers and filters were inert here).
  const campaignTypes = useAtomValue(consolidatedPerformanceCampaignTypesAtom);
  const selectedCampaignIds = useAtomValue(consolidatedPerformanceCampaignIdsAtom);
  const includeExternal = useAtomValue(consolidatedPerformanceIncludeExternalAtom);
  const typeFilter = useMemo<(type: string) => boolean>(() => {
    if (campaignTypes.size === 0 || campaignTypes.has('all')) return () => true;
    return (type: string) => campaignTypes.has(type);
  }, [campaignTypes]);
  const { scopedCampaigns, scopedGoogleAdsIds } = useScopedCampaigns({ includeExternal, typeFilter, selectedCampaignIds });

  // #6278 — Meta money renders in the Meta AD ACCOUNT's currency (the Audience
  // panel's #6182 sourcing); one org can run differently-billed accounts.
  // Enabled travels through the factory (#6489) so its org-seeded gate holds.
  const { data: metaStatus } = useQuery(metaIntegrationStatusQueryOptions(metaInScope));
  const { symbol: metaSymbol } = useCurrencySymbol(undefined, metaStatus?.selected_ad_account?.currency);
  const metaCs = metaSymbol ?? cs;
  const symFor = (p: PivotRow['platform']) => (p === 'meta' ? metaCs : cs);


  const { data: adData, isLoading: googleLoading, isError: googleError } = useAdPerformanceReportQuery(
    scopedGoogleAdsIds || undefined,
    undefined,
    {
      startDate: window.start_date,
      endDate: window.end_date,
      // Disabled when the filter matches nothing — an absent campaign_ids
      // param means account-wide to the backend (#6275).
      enabled: googleInScope && scopedGoogleAdsIds.length > 0,
    },
  );
  const { data: metaResp, isLoading: metaLoading, isError: metaError } = useQuery(metaCreativePerformanceQueryOptions(metaInScope));

  // The org campaigns list is light (#4760, no nested ads) — the draft
  // seeding's ads come from the per-campaign detail fan-out.
  const neverLiveAds = useNeverLiveAds(scopedCampaigns, googleInScope);

  // Platform grouping omitted — the top-level scope switch already covers it.
  const dimensions = useMemo(() => availableDimensions(scope, ['ad', 'creative']), [scope]);
  const [groupBy, setGroupBy] = useState<PivotDimension>('ad');
  const activeDimension = forcedDimension ?? (dimensions.includes(groupBy) ? groupBy : (dimensions[0] ?? 'ad'));

  // Flatten both platforms into ad-level leaves; keep Meta-only extras in a side map
  // keyed by the ad's pivot key so pivot.ts stays platform-agnostic.
  const { leaves, extras } = useMemo(() => {
    const out: PivotLeaf[] = [];
    const ex = new Map<string, AdExtras>();
    if (googleInScope) {
      // Campaign slug per Google campaign id, for the row click-through.
      const slugByGid = new Map<string, string>();
      for (const c of scopedCampaigns) {
        const gid = c.googleads_campaign?.googleads_campaign_id;
        if (gid != null && c.slug) slugByGid.set(String(gid), c.slug);
      }
      const reported = new Set<string>();
      for (const ad of adData ?? []) {
        reported.add(String(ad.campaign_id));
        const slug = slugByGid.get(String(ad.campaign_id));
        out.push({
          platform: 'google',
          campaignId: ad.campaign_id,
          campaignName: ad.campaign_name || '—',
          adGroupId: ad.ad_group_id,
          adGroupName: ad.ad_group_name || ad.ad_group_id,
          adId: ad.ad_id,
          adName: ad.ad_name || ad.headlines?.[0] || ad.ad_id,
          status: null,
          impressions: ad.impressions ?? 0,
          clicks: ad.clicks ?? 0,
          results: ad.conversions ?? 0,
          spend: ad.cost ?? 0,
          conversionValue: ad.all_conversions_value ?? 0,
        });
        ex.set(`google:${ad.ad_id}`, {
          frequency: null, status: null, campaign: ad.campaign_name || null,
          purchases: 0, costPerPurchaseCents: null,
          href: slug ? `/campaigns/google/${slug}` : null,
        });
      }
      // Never-live campaigns' local ads seed rows too (ruling 09-09): drafts
      // exist and should list, with dashes for the unmeasured sums. Report
      // rows take precedence per campaign.
      for (const c of scopedCampaigns) {
        const gid = c.googleads_campaign?.googleads_campaign_id;
        if (gid != null && reported.has(String(gid))) continue;
        const unmeasured = gid == null || c.state === 'draft';
        const ads = c.ads?.length ? c.ads : (neverLiveAds.get(c.id) ?? []);
        ads.forEach((ad, i) => {
          const key = `glitch-ad-${ad.id}`;
          out.push({
            platform: 'google',
            campaignId: gid != null ? String(gid) : `glitch-${c.id}`,
            campaignName: parseCampaignName(c.name),
            adGroupId: null,
            adGroupName: null,
            adId: key,
            adName: ad.name ?? `Ad ${i + 1}`,
            status: null,
            impressions: 0,
            clicks: 0,
            results: 0,
            spend: 0,
            conversionValue: null,
            unmeasured,
          });
          ex.set(`google:${key}`, {
            frequency: null, status: null, campaign: parseCampaignName(c.name),
            purchases: 0, costPerPurchaseCents: null,
            // Seeded rows know their Glitch ad id, so they deep-link to the
            // ad itself rather than just its campaign.
            href: c.slug ? `/campaigns/google/${c.slug}/ad/${ad.id}` : null,
          });
        });
      }
    }
    if (metaInScope && metaResp) {
      for (const m of metaResp.rows) {
        out.push({
          platform: 'meta',
          campaignId: '',
          campaignName: '—',
          adGroupId: null,
          adGroupName: null,
          adId: m.meta_ad_id,
          adName: m.headline || m.name || m.meta_ad_id,
          assetId: m.creative_asset_id ?? null,
          assetName: m.headline || m.name || null,
          status: null,
          impressions: m.impressions,
          clicks: m.clicks,
          results: m.leads,
          spend: toDisplayAmount(m.spend_cents, 'meta'),
          conversionValue: null,
        });
        ex.set(`meta:${m.meta_ad_id}`, { frequency: numOrNull(m.frequency), status: m.performance_status ?? null, campaign: null, purchases: m.purchases, costPerPurchaseCents: m.cost_per_purchase_cents ?? null, assetId: m.creative_asset_id ?? null });
      }
    }
    return { leaves: out, extras: ex };
  }, [googleInScope, adData, scopedCampaigns, neverLiveAds, metaInScope, metaResp]);

  const rows = useMemo(() => pivotLeaves(leaves, activeDimension), [leaves, activeDimension]);

  const columns = useMemo<ColumnDef<PivotRow, unknown>[]>(() => {
    const isAd = activeDimension === 'ad';
    const cols: ColumnDef<PivotRow, unknown>[] = [];
    // Thin, sortable platform-logo column pinned to the left of the creative name —
    // only under All scope (mixed platforms).
    if (scope === 'all') {
      cols.push({
        id: 'platform',
        accessorFn: (r) => r.platform,
        header: '',
        cell: ({ row }) => <PlatformLogo platform={row.original.platform} className="h-4 w-4" />,
        meta: { className: 'pl-4 pr-1 w-8' },
      });
    }
    cols.push({
      accessorKey: 'label',
      header: PIVOT_DIMENSIONS[activeDimension].columnHeader,
      cell: ({ row }) => {
        const ex = isAd ? extras.get(row.original.key) : undefined;
        const campaign = ex?.campaign ?? null;
        const href = ex?.href ?? null;
        // Meta creatives lead with their visual: the ad's image is what the
        // audience actually saw, so a leaderboard of text labels answers the
        // wrong question (09-09).
        const thumbAssetId = isAd ? ex?.assetId : row.original.assetId;
        const showThumb = row.original.platform === 'meta' && (isAd || activeDimension === 'creative');
        return (
          <div className={cn('flex min-w-0 items-center', showThumb && 'gap-2.5')}>
          {showThumb && <CreativeThumb assetId={thumbAssetId} alt={row.original.label} />}
          <div className="min-w-0">
            {href ? (
              // Ads link into the app (09-09): seeded rows to the ad itself,
              // reported rows to their campaign. Meta rows have no Glitch
              // record to link to and stay plain text. The affordance is a
              // quiet cobalt shift on hover — no underline, no resting
              // colour: a table of blue rows reads as noise (ruling 09-09).
              <Link to={href} className="block truncate font-medium text-gray-900 transition-colors hover:text-[#290BD9] hover:underline">
                {row.original.label}
              </Link>
            ) : (
              <p className="truncate font-medium text-gray-900">{row.original.label}</p>
            )}
            {campaign && <p className="truncate text-xs text-gray-500">{campaign}</p>}
          </div>
          </div>
        );
      },
    });
    // A never-live row's sums are "—", never 0 (ruling 09-09): nothing was
    // measured. The derived ratios already dash via their null denominators.
    const dashIfUnmeasured = (row: PivotRow, render: () => React.ReactNode) =>
      row.unmeasured ? <MutedDash /> : render();
    cols.push(
      // Layer 2 — volume
      { accessorKey: 'impressions', header: 'Impr.', cell: ({ row }) => dashIfUnmeasured(row.original, () => row.original.impressions.toLocaleString()) },
      { accessorKey: 'clicks', header: 'Clicks', cell: ({ row }) => dashIfUnmeasured(row.original, () => row.original.clicks.toLocaleString()) },
      {
        accessorKey: 'results',
        header: scope === 'meta' ? 'Leads' : 'Conv.',
        cell: ({ row }) =>
          dashIfUnmeasured(row.original, () =>
            scope === 'meta'
              ? Math.round(row.original.results).toLocaleString()
              : row.original.resultKind === 'leads'
                ? `${Math.round(row.original.results).toLocaleString()} leads`
                : Math.round(row.original.results).toLocaleString()),
      },
      // Layer 1 — money (ROAS is "—" for Meta until #6044)
      { accessorKey: 'spend', header: 'Cost', cell: ({ row }) => dashIfUnmeasured(row.original, () => formatMoney(row.original.spend, symFor(row.original.platform))) },
      { id: 'roas', accessorFn: (r) => deriveMetrics(r).roas, header: 'ROAS', cell: ({ row }) => formatRoas(deriveMetrics(row.original).roas) },
      { id: 'cpc', accessorFn: (r) => deriveMetrics(r).cpc, header: 'CPC', cell: ({ row }) => formatMoney(deriveMetrics(row.original).cpc, symFor(row.original.platform)) },
      // Layer 3 — rates
      { id: 'ctr', accessorFn: (r) => deriveMetrics(r).ctr, header: 'CTR', cell: ({ row }) => formatRate(deriveMetrics(row.original).ctr, 2) },
      { id: 'imprToConv', accessorFn: (r) => deriveMetrics(r).imprToConv, header: 'Impr→Conv.', cell: ({ row }) => formatRate(deriveMetrics(row.original).imprToConv, 2) },
      { id: 'clickToConv', accessorFn: (r) => deriveMetrics(r).clickToConv, header: 'Conv. rate', cell: ({ row }) => formatRate(deriveMetrics(row.original).clickToConv, 1) },
      // Efficiency
      {
        id: 'cpa',
        accessorFn: (r) => (r.results > 0 ? r.spend / r.results : null),
        header: scope === 'meta' ? 'Cost / lead' : 'Cost / conv.',
        cell: ({ row }) => (row.original.results > 0 ? formatMoney(row.original.spend / row.original.results, symFor(row.original.platform)) : '—'),
      },
    );
    // Meta-native depth: only meaningful per-ad and only under Meta scope (Google rows
    // have no frequency/verdict). Preserves the pre-pivot Meta creative view.
    if (isAd && scope === 'meta') {
      // Purchases are Meta pixel/CAPI e-commerce events — only show the columns when the
      // account actually has any (lead-gen accounts would otherwise carry an all-zero column).
      const hasPurchases = [...extras.values()].some((e) => e.purchases > 0);
      if (hasPurchases) {
        cols.push(
          {
            id: 'purchases',
            header: 'Purchases',
            cell: ({ row }) => (extras.get(row.original.key)?.purchases ?? 0).toLocaleString(),
          },
          {
            id: 'costPerPurchase',
            header: 'Cost / purch.',
            cell: ({ row }) => {
              const c = extras.get(row.original.key)?.costPerPurchaseCents;
              return c != null ? formatMoney(toDisplayAmount(c, 'meta'), cs) : '—';
            },
          },
        );
      }
      cols.push(
        {
          id: 'frequency',
          header: 'Freq.',
          cell: ({ row }) => {
            const f = extras.get(row.original.key)?.frequency;
            return f != null ? f.toFixed(2) : '—';
          },
        },
        {
          id: 'status',
          header: 'Status',
          cell: ({ row }) => {
            const s = extras.get(row.original.key)?.status;
            return s ? <MetaPerformanceBadge status={s} /> : '—';
          },
        },
      );
    }
    return cols;
  }, [cs, metaCs, scope, activeDimension, extras]);

  const {
    visible: visiblePivotColumns, toggle: togglePivotColumn, reset: resetPivotColumns,
  } = useColumnVisibility({
    settingsKey: 'ads_pivot',
    allIds: ADS_PIVOT_COLUMN_IDS,
    pinnedIds: ADS_PIVOT_PINNED_IDS,
  });
  const shownColumns = useMemo(
    () =>
      columns.filter((c) => {
        const id = pivotColId(c);
        return !ADS_PIVOT_COLUMN_IDS.has(id) || ADS_PIVOT_PINNED_IDS.has(id) || visiblePivotColumns.has(id);
      }),
    [columns, visiblePivotColumns],
  );
  // Built from the rendered columns so labels track scope and dimension; the
  // picker and bubble count against the columns PRESENT at this scope.
  const pivotColumnsMeta = useMemo(
    () =>
      columns.map((c) => {
        const id = pivotColId(c);
        return {
          id,
          label: typeof c.header === 'string' && c.header ? c.header : id === 'platform' ? 'Platform' : id,
          pinned: ADS_PIVOT_PINNED_IDS.has(id),
        };
      }),
    [columns],
  );
  const presentPivotIds = useMemo(
    () => new Set(columns.map(pivotColId).filter((id) => ADS_PIVOT_COLUMN_IDS.has(id))),
    [columns],
  );
  const visiblePresentIds = useMemo(
    () => new Set([...presentPivotIds].filter((id) => visiblePivotColumns.has(id))),
    [presentPivotIds, visiblePivotColumns],
  );

  const loading = (googleInScope && googleLoading) || (metaInScope && metaLoading);
  if (loading) {
    return <div className="flex h-24 items-center justify-center text-sm text-gray-400">Loading…</div>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        {toolbarLeading}
        {!forcedDimension && dimensions.length > 1 && (
          <SubViewSwitcher
            items={dimensions.map((d) => ({ id: d, label: PIVOT_DIMENSIONS[d].label }))}
            active={activeDimension}
            onChange={setGroupBy}
          />
        )}
        <div className="ml-auto">
          <ColumnsPopover
            columnsMeta={pivotColumnsMeta}
            visibleIds={visiblePresentIds}
            totalIds={presentPivotIds}
            onToggle={togglePivotColumn}
            onReset={resetPivotColumns}
          />
        </div>
      </div>
      <DataTable
        data={rows}
        columns={shownColumns}
        pageSize={15}
        emptyState={
          // #6496 — a failed report must not read as an empty account.
          (googleInScope && googleError) || (metaInScope && metaError)
            ? 'The report could not be loaded. Refresh to retry.'
            : metaInScope && !googleInScope
              // A never-synced integration is not the same truth as a synced
              // one with zero activity (09-08): say which it is.
              ? metaResp && metaResp.window == null
                ? 'Meta insights have not synced yet. Ads appear after the first sync.'
                : 'No Meta ads with activity in the synced window.'
              : 'No ads with activity in this period'
        }
      />
    </div>
  );
}

/**
 * The Google-specific Creative & Ads view, reusing perf-v2's AdsTab verbatim
 * (Ads / Headlines / Landing Pages sub-views + column picker). Fed by the shared
 * usePerformanceData hook (the same data layer /performance-v2 uses) so it honors
 * the toolbar filters (#5995) with no duplicated computation. Owns the sub-view
 * toggle + Ads column picker locally; the chart is deferred (renderChart => null,
 * performance-chart widget omitted from the layout) as on the Overview.
 */
function GoogleAdsSection({
  getTabLayout,
  onReorder,
  onResize,
  forcedSubView,
}: {
  getTabLayout: (tabId: string) => WidgetConfig[];
  onReorder: (tabId: string, layout: WidgetConfig[]) => void;
  onResize: (tabId: string, id: string, width: 33 | 50 | 66 | 100) => void;
  /** All-scope hosting (09-08): the outer switcher owns the sub-view and this
      section renders exactly that view without its own toolbar. */
  forcedSubView?: AdsSubView;
}) {
  const { symbol } = useCurrencySymbol('google');
  const cs = symbol ?? '';

  const [ownSubView, setSubView] = useState<AdsSubView>('ads');
  const subView = forcedSubView ?? ownSubView;

  const {
    visible: visibleAdsColumns, toggle: toggleAdsColumn, reset: resetAdsColumns,
  } = useColumnVisibility({ settingsKey: 'ads', allIds: ADS_COLUMN_IDS, pinnedIds: ADS_PINNED_COLUMN_IDS });

  // Driven by the shared filter toolbar (#5971/#5995).
  const selectedDays = useAtomValue(consolidatedPerformanceDateRangeAtom);
  const customRange = useAtomValue(consolidatedPerformanceCustomRangeAtom);
  const groupBy = useAtomValue(consolidatedPerformanceGroupByAtom);
  const comparison = useAtomValue(consolidatedPerformanceComparisonAtom);
  const campaignTypes = useAtomValue(consolidatedPerformanceCampaignTypesAtom);
  const selectedCampaignIds = useAtomValue(consolidatedPerformanceCampaignIdsAtom);
  const includeExternal = useAtomValue(consolidatedPerformanceIncludeExternalAtom);

  const typeFilter = useMemo<(type: string) => boolean>(() => {
    if (campaignTypes.size === 0 || campaignTypes.has('all')) return () => true;
    return (type: string) => campaignTypes.has(type);
  }, [campaignTypes]);

  const perf = usePerformanceData({
    includeExternal,
    tab: 'ads',
    campaignSubView: 'by-type',
    comparisonMode: comparison,
    selectedCampaignIds,
    chartDateRange: selectedDays,
    customRange,
    chartGroupBy: groupBy,
    showUserEdits: false,
    selectedMetric: 'conversions',
    selectedDays,
    typeFilter,
    cs,
  });

  return (
    <div className="space-y-3">
      {/* All-scope hosting hides this toolbar: the outer switcher owns the
          sub-view there (09-08). */}
      {!forcedSubView && (
        <div className="flex items-center gap-2">
          <SubViewSwitcher
            items={[
              { id: 'ads' as const, label: 'Ads' },
              { id: 'headlines' as const, label: 'Headlines' },
              { id: 'landing-pages' as const, label: 'Landing Pages' },
            ]}
            active={subView}
            onChange={setSubView}
          />
          {/* Right-aligned like every table toolbar control (ruling 09-08). */}
          {subView === 'ads' && (
            <div className="ml-auto">
              <ColumnsPopover
                columnsMeta={ADS_TABLE_COLUMNS}
                visibleIds={visibleAdsColumns}
                totalIds={ADS_COLUMN_IDS}
                onToggle={toggleAdsColumn}
                onReset={resetAdsColumns}
              />
            </div>
          )}
        </div>
      )}
      <AdsTab
        subView={subView}
        adMetrics={perf.adMetrics}
        previousAdMetricsByAdId={perf.previousAdMetricsByAdId}
        activeCampaigns={perf.activeCampaigns}
        scopedCampaignIds={perf.campaignIds}
        visibleColumns={visibleAdsColumns}
        currencySymbol={cs}
        dateRangeLabel={perf.dateRangeLabel}
        dateRangeFilter={perf.dateRangeFilter}
        layout={getTabLayout(GOOGLE_ADS_LAYOUT_KEY)}
        onReorder={(l) => onReorder(GOOGLE_ADS_LAYOUT_KEY, l)}
        onResize={(id, w) => onResize(GOOGLE_ADS_LAYOUT_KEY, id, w)}
        renderChart={() => null}
      />
    </div>
  );
}

/**
 * Consolidated Creative & Ads tab (#5995) — hybrid by scope. Google renders the
 * full-depth perf-v2 AdsTab (Ads / Headlines / Landing Pages sub-views, column
 * picker) fed by the shared usePerformanceData hook; Meta / All keep the existing
 * merged cross-platform CreativeAdsPanel table unchanged.
 */
export function ConsolidatedCreativeAdsPanel({
  scope,
  getTabLayout,
  onReorder,
  onResize,
}: {
  scope: PlatformScope;
  getTabLayout: (tabId: string) => WidgetConfig[];
  onReorder: (tabId: string, layout: WidgetConfig[]) => void;
  onResize: (tabId: string, id: string, width: 33 | 50 | 66 | 100) => void;
}) {
  // Hooks stay above the early returns.
  const [allSubView, setAllSubView] = useState<WrapperView>('ads');
  if (scope === 'google') {
    return <GoogleAdsSection getTabLayout={getTabLayout} onReorder={onReorder} onResize={onResize} />;
  }
  // One unified switcher listing every view (ruling 09-08). Creatives is
  // Meta-only — Google's Search ads carry no creative asset — and Headlines /
  // Landing Pages are Google concepts; each is marked when the scope mixes.
  const mark = scope === 'all';
  const items: { id: WrapperView; label: string; platform?: Platform }[] = [
    { id: 'ads', label: 'Ads' },
    { id: 'creatives', label: 'Creatives', platform: mark ? 'meta' : undefined },
    ...(scope === 'all'
      ? ([
          { id: 'headlines' as const, label: 'Headlines', platform: 'google' as const },
          { id: 'landing-pages' as const, label: 'Landing Pages', platform: 'google' as const },
        ])
      : []),
  ];
  const switcher = <SubViewSwitcher items={items} active={allSubView} onChange={setAllSubView} />;
  // The switcher rides the merged table's toolbar row on the pivot views so
  // the Columns control never drops a row (09-08); the Google views have no
  // toolbar of their own, so it leads them directly.
  if (allSubView === 'ads' || allSubView === 'creatives') {
    return (
      <CreativeAdsPanel
        scope={scope}
        toolbarLeading={switcher}
        forcedDimension={allSubView === 'creatives' ? 'creative' : 'ad'}
      />
    );
  }
  return (
    <div className="space-y-3">
      {switcher}
      <GoogleAdsSection
        getTabLayout={getTabLayout}
        onReorder={onReorder}
        onResize={onResize}
        forcedSubView={allSubView as AdsSubView}
      />
    </div>
  );
}
